import React, {useState, useEffect, useCallback} from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import FacebookIcon from '@material-ui/icons/Facebook';
// import AppleIcon from '@material-ui/icons/Apple';
import fetchJSON from '../utils/fetchJSON';
import getCSRFToken from '../utils/gaia/getCSRFToken';
import gettext from '../utils/django/gettext';
import {GRANITE_6, GRANITE_3, GRANITE_1} from '../colors';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import {theme} from '../styles';
import Typography from '@material-ui/core/Typography';
import qs from 'qs';
import useSyncPromise from '../utils/react/useSyncPromise';
import useUser from '../hooks/useUser';

interface Response {
    code: number;
}

// eslint-disable-next-line prefer-arrow-callback
export default React.forwardRef(function LoginForm(
    {
        nextPage,
        setForm,
        style,
        onSubmitDone,
        onUserAuthenticated,
        analyticsContext,
    }: AuthFormProps,
    ref: React.Ref<HTMLDivElement>
) {
    const [, updateUser] = useUser();
    const [state, setState] = useState({email: '', password: ''});

    const [responsePromise, setResponsePromise] = useState<
        Promise<Response> | undefined
    >();
    const [response, error, isResponseLoaded] = useSyncPromise(
        responsePromise,
        undefined
    );
    const isLoaded = response ? response.code !== 1000 : isResponseLoaded;

    const onRegisterClick = useCallback(() => {
        if (setForm) {
            setForm('register');
        } else {
            window.location.href = `/register/?next=${nextPage}`;
        }
    }, [setForm, nextPage]);

    function onSubmit(event: React.FormEvent) {
        event.preventDefault();
        if (!isLoaded) return;

        const csrf = getCSRFToken();

        setResponsePromise(
            fetchJSON('/register/addDevice/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    ...(csrf && {'X-CSRFToken': csrf}),
                    Accept: 'application/json',
                },
                body: qs.stringify({
                    email: state.email,
                    password: state.password,
                    analytics_context: analyticsContext,
                }),
            })
        );
    }

    useEffect(() => {
        if ((response || error) && onSubmitDone) onSubmitDone();
    }, [error, onSubmitDone, response]);

    useEffect(() => {
        if (response?.code === 1000) {
            updateUser();
            if (onUserAuthenticated) {
                onUserAuthenticated();
            }
        }
        // We don't want to run this effect again if the value of "onUserAuthenticated" changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [response]);

    return (
        <div style={{width: 275, ...style}} ref={ref}>
            <Typography
                variant='h4'
                style={{
                    fontWeight: 'bold',
                    color: GRANITE_1,
                    marginBottom: theme.spacing(2),
                }}
            >
                {gettext('Log In')}
            </Typography>

            <form autoComplete='off' autoCapitalize='off' onSubmit={onSubmit}>
                <TextField
                    id='login-email'
                    style={{display: 'block', marginBottom: theme.spacing(2)}}
                    fullWidth
                    label={gettext('Email')}
                    type='email'
                    variant='outlined'
                    autoFocus
                    required
                    value={state.email}
                    onChange={(e) =>
                        setState({...state, email: e.target.value})
                    }
                    error={response?.code === 1020}
                    helperText={
                        response?.code === 1020 &&
                        gettext('Email address not found')
                    }
                    InputLabelProps={{required: false}}
                />
                <TextField
                    id='login-password'
                    style={{display: 'block', marginBottom: theme.spacing(2)}}
                    fullWidth
                    label={gettext('Password')}
                    type='password'
                    variant='outlined'
                    required
                    value={state.password}
                    inputProps={{minLength: 8}}
                    onChange={(e) =>
                        setState({...state, password: e.target.value})
                    }
                    error={response?.code === 1040}
                    helperText={
                        <Link
                            href='/user/password/reset/?next=/login/'
                            style={{
                                color:
                                    response?.code === 1020
                                        ? 'secondary'
                                        : 'inherit',
                            }}
                            target='_blank'
                            rel='noopener'
                        >
                            {response?.code === 1040 &&
                                gettext('Incorrect password - ')}
                            {gettext('Forgot your password?')}
                        </Link>
                    }
                    InputLabelProps={{required: false}}
                />

                {error && (
                    <Typography
                        variant='body1'
                        color='secondary'
                        id='unknown-error'
                        style={{paddingBottom: theme.spacing(2)}}
                    >
                        An unknown error occurred. Please try again.
                    </Typography>
                )}

                <Button
                    type='submit'
                    fullWidth
                    variant='contained'
                    size='large'
                    color='primary'
                    disabled={!isLoaded}
                    style={{fontSize: 14}}
                >
                    {!isLoaded ? (
                        <CircularProgress
                            thickness={7}
                            size={24}
                            disableShrink
                        />
                    ) : (
                        gettext('Log In')
                    )}
                </Button>
            </form>

            <Link
                onClick={onRegisterClick}
                style={{
                    marginTop: theme.spacing(2),
                    display: 'block',
                    width: '100%',
                    textAlign: 'center',
                    fontSize: 14,
                }}
            >
                {gettext('Sign up for free')}
            </Link>

            <div
                style={{
                    textAlign: 'center',
                    borderTop: '1px solid',
                    borderTopColor: GRANITE_6,
                    height: 1,
                    marginTop: theme.spacing(3),
                    marginBottom: theme.spacing(3),
                }}
            >
                <div
                    style={{
                        padding: '0 12px',
                        color: GRANITE_3,
                        backgroundColor: '#fff',
                        display: 'inline-block',
                        transform: 'translateY(-77%)',
                        fontSize: 12,
                    }}
                >
                    or
                </div>
            </div>

            <Button
                component='a'
                variant='contained'
                fullWidth
                size='large'
                style={{
                    backgroundColor: '#3b5998',
                    color: '#fff',
                    fontSize: 14,
                }}
                startIcon={<FacebookIcon />}
                href={`/login/facebook/?next=${nextPage}`}
                disabled={!isLoaded}
                target='_blank'
                rel='noopener'
            >
                {gettext('Log In with Facebook')}
            </Button>

            {/* <Button
            component='a'
            variant='contained'
            fullWidth
            size='large'
            style={{
                backgroundColor: '#000000',
                color: '#FFFFFF',
                fontSize: 14,
                marginTop: theme.spacing(2),
            }}
            startIcon={<AppleIcon />}
            href={'https://appleid.apple.com/auth/authorize?client_id=com.gaiagps.www' +
            '&redirect_uri=https%3A%2F%2Fwww.gaiagps.com%2Fapple-signin%2F&' +
            'response_type=code id_token' +
            '&scope=email&response_mode=form_post'}
            disabled={!isLoaded}
            target='_blank'
            rel='noopener'
        >
            {gettext('Log In with Apple')}
        </Button> */}
        </div>
    );
});
